import { Popover } from '@mui/material'
import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import config from 'config'
import { user } from 'ampersand-app'
import LogoutIcon from '@assets/images/icons/logout.svg'
import ThemeInterface from '@/theme-interface'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import UserHeader from '@/modules/layout/components/menu/header/user-header'
import AccountPopoverFooter from './account-popover-footer'
import AccountPopoverUpgradeBox from './account-popover-upgrade-box'
import { userLoggedOut } from '@/redux/ducks/global'
import AccountPopoverItem from './account-popover-item'
import { ColorModes } from '@/modules/layout/constants'
import { useNavigate } from 'react-router'
import { isMeinBuero } from '@/utils/build-env'

const isMB = isMeinBuero()

const { faqUrl } = config

const useStyles = makeStyles((theme: ThemeInterface) => ({
  popover: {
    boxShadow: '0 3px 20px 0 rgba(0, 0, 0, 0.33)',
    color: theme.mode.color.default,
    backgroundColor: theme.mode.backgroundColor.default,
  },
  paper: {
    width: 400,
  },
  header: {
    padding: '30px 30px 0px 30px',
    alignItems: 'center',
    marginBottom: 20,
  },
  greeting: {
    fontSize: theme.fontSizes.smedium,
    color: theme.mode.color.default,
    maxWidth: 280,
  },
  info: {
    fontSize: theme.fontSizes.smaller,
  },
  email: {
    color: theme.mode.color.medium,
  },
  seperator: {
    borderBottom: `1px solid ${theme.mode.borderColor.default}`,
    opacity: theme.mode.type === ColorModes.dark ? 0.2 : 1.0,
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 0px',
  },
  logoutIcon: {
    fill: theme.mode.color.default,
  },
  logout: {
    padding: '20px 32px',
    '&:hover': {
      color: theme.error,
      '& $logoutIcon': {
        fill: theme.error,
      },
    },
  },
}), { name: 'AccountPopover' })

interface AccountPopoverProps {
  open: boolean,
  hasInvites: boolean,
  isTaxAccountant: boolean,
  anchorEl: Element,
  transformOrigin: { horizontal: number, vertical: number },
  onClose: () => void
}

const AccountPopover = (props: AccountPopoverProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    open,
    hasInvites,
    isTaxAccountant,
    anchorEl,
    transformOrigin,
    onClose,
  } = props
  const transform = transformOrigin || { horizontal: -65, vertical: 0 }
  const { experimentalFeatures: activeExperimentalFeatures } = user
  const items = useMemo(() => {
    return [
      {
        name: 'manage-item',
        title: lang.myAccount.popover.items.manage,
        onClick: () => {
          navigate('/my-account')
          onClose()
        },
        isNew: false,
      },
      {
        name: 'faq-item',
        title: lang.myAccount.popover.items.faq,
        onClick: () => {
          window.open(faqUrl, '_blank')
          onClose()
        },
        isNew: false,
      },
      ...(isMB
        ? []
        : [{
          name: 'ai-settings',
          title: lang.myAccount.popover.items.aiSettings,
          onClick: () => {
            navigate('/settings/myaccount#aiSettings')
            onClose()
          },
          isNew: true,
        }]),
    ]
  }, [activeExperimentalFeatures])

  const logoutItem = useMemo(() => {
    return (
      <AccountPopoverItem
        name='logout-item'
        title={lang.myAccount.popover.items.logout}
        isNew={false}
        styles={{ root: classes.logout }}
        icon={<SVGIcon svg={LogoutIcon} className={classes.logoutIcon} height='12px' width='12px' /> }
        onClick={() => {
          dispatch(userLoggedOut())
          user.logout(true)
        }}
      />
    )
  }, [])

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      classes={{ paper: classes.popover }}
      transformOrigin={transform}
      anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
      <div className={classes.paper}>
        <UserHeader
          useNewTenantAvatar
          hasInvites={hasInvites}
          isTaxConsultant={isTaxAccountant}
          customClass={{
            header: classes.header,
            greeting: classes.greeting,
            confirmEmail: classes.info,
            email: classes.email,
          }} />
        <div className={classes.seperator} />
        {!isMB && <AccountPopoverUpgradeBox onClose={onClose} />}
        <div className={classes.items}>
          {items.map((item) => {
            return (
              <AccountPopoverItem
                key={item.name}
                name={item.name}
                isNew={item.isNew}
                title={item.title}
                onClick={item.onClick}
              />
            )
          })}
        </div>
        <div className={classes.seperator} />
        {logoutItem}
        <AccountPopoverFooter />
      </div>
    </Popover >
  )
}

export default AccountPopover
